import { toast } from "react-toastify/unstyled";
import { PROJECTS_ENDPOINT } from "Constants";
import {
  GetPackageRequest,
  GetVersionRequest,
  GetVersionResponse,
  GetVersionsRequest,
  GetVersionsResponse,
  GroupUpdatesMessage,
  Package,
  ProgressUpdateMessage,
  ReprocessVersionRequest,
  SearchProjectsRequest,
  SearchProjectsResponse,
  UpdatePackageRequest,
  UpdateVersionRequest,
} from "Types";
import { clearVersions, sortProjects, sortVersions } from "Utils";
import { baseApi as api } from "./base.api";
import {
  listenToProgressUpdates,
  updateGetVersionsCache,
  updateProgress,
  updateProjectSearchCache,
} from "./projectsWebsocketUpdates";
import { listenToGroupUpdates } from "./websocket";

/** IMPORTANT: All cache updates are done through websockets,
 * there is no need to use provide/invalidate tags.
 *
 * In case you need to add a new API, make sure it has websocket support.
 */

export const projectsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getProjectsSearch: build.query<
      SearchProjectsResponse,
      SearchProjectsRequest
    >({
      query: (queryArg) => ({
        url: `${PROJECTS_ENDPOINT}/search`,
        params: {
          search_name: queryArg.searchName,
          group_id: queryArg.groupId,
          limit: queryArg.limit,
          offset: queryArg.offset,
        },
      }),
      transformResponse: (data: SearchProjectsResponse) => ({
        match_count: data.match_count,
        projects: sortProjects(data.projects),
      }),
      async onCacheEntryAdded(
        arg,
        {
          updateCachedData,
          cacheDataLoaded,
          cacheEntryRemoved,
          getState,
          dispatch,
        },
      ) {
        const notifications: Array<string | number> = [];

        const listener = (event: MessageEvent) => {
          const data = JSON.parse(event.data) as GroupUpdatesMessage;

          updateCachedData((draft) => {
            return updateProjectSearchCache(
              getState(),
              dispatch,
              draft,
              data,
              arg,
              notifications,
            );
          });
        };

        await listenToGroupUpdates(
          cacheDataLoaded,
          cacheEntryRemoved,
          listener,
          () => {
            notifications.forEach((notificationId) =>
              toast.dismiss(notificationId),
            );
          },
        );
      },
    }),
    getPackage: build.query<Package, GetPackageRequest>({
      query: (queryArg) => ({
        url: `${PROJECTS_ENDPOINT}/packages/${queryArg.packageId}`,
      }),
      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        const listener = (event: MessageEvent) => {
          const message = JSON.parse(event.data) as GroupUpdatesMessage;

          if (message.entity === "package" && message.action === "updated") {
            updateCachedData((draft) => {
              const updatedPackage = message.data.find(
                (item) => item.package_id === arg.packageId,
              );

              return updatedPackage !== undefined ? updatedPackage : draft;
            });
          }
        };

        await listenToGroupUpdates(
          cacheDataLoaded,
          cacheEntryRemoved,
          listener,
        );
      },
    }),
    updatePackage: build.mutation<void, UpdatePackageRequest>({
      query: (queryArg) => ({
        url: `${PROJECTS_ENDPOINT}/packages/${queryArg.packageId}`,
        method: "PATCH",
        body: queryArg.body,
      }),
      invalidatesTags: (_result, _error, { packageId }) => [
        { type: "Package", id: packageId },
        "SoftwareCatalog",
      ],
    }),
    getVersions: build.query<GetVersionsResponse, GetVersionsRequest>({
      query: (queryArg) => ({
        url: `${PROJECTS_ENDPOINT}/versions`,
        params: { package_id: queryArg.packageId },
      }),
      transformResponse: (data: GetVersionsResponse) => ({
        match_count: data.match_count,
        max_versions_per_package: data.max_versions_per_package,
        versions: sortVersions(data.versions),
      }),
      async onCacheEntryAdded(
        arg,
        {
          updateCachedData,
          cacheDataLoaded,
          cacheEntryRemoved,
          getState,
          dispatch,
        },
      ) {
        const groupUpdatesListener = (event: MessageEvent) => {
          const data = JSON.parse(event.data) as GroupUpdatesMessage;

          updateCachedData((draft) =>
            updateGetVersionsCache(getState(), dispatch, draft, data, arg),
          );
        };
        const progressUpdateListener = (event: MessageEvent) => {
          const message = JSON.parse(event.data) as ProgressUpdateMessage;

          if (message.entity === "version") {
            updateCachedData((draft) => updateProgress(draft, message));
          }
        };

        await Promise.allSettled([
          listenToGroupUpdates(
            cacheDataLoaded,
            cacheEntryRemoved,
            groupUpdatesListener,
            () => {
              dispatch(clearVersions());
            },
          ),
          listenToProgressUpdates(
            cacheDataLoaded,
            cacheEntryRemoved,
            arg.packageId,
            progressUpdateListener,
          ),
        ]);
      },
    }),
    getVersion: build.query<GetVersionResponse, GetVersionRequest>({
      query: (queryArg) => ({
        url: `${PROJECTS_ENDPOINT}/versions/${queryArg.versionId}`,
      }),
      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        const listener = (event: MessageEvent) => {
          const message = JSON.parse(event.data) as GroupUpdatesMessage;

          if (message.entity === "version" && message.action === "updated") {
            updateCachedData((draft) => {
              const updatedVersion = message.data.find(
                (version) => version.version_id === arg.versionId,
              );

              return updatedVersion !== undefined ? updatedVersion : draft;
            });
          }
        };

        await listenToGroupUpdates(
          cacheDataLoaded,
          cacheEntryRemoved,
          listener,
        );
      },
    }),
    reprocessVersion: build.mutation<void, ReprocessVersionRequest>({
      query: (queryArg) => ({
        url: `${PROJECTS_ENDPOINT}/versions/${queryArg.versionId}/reprocess`,
        params: { notify_on_completion: queryArg.notify_on_completion },
        method: "POST",
      }),
    }),
    updateVersion: build.mutation<void, UpdateVersionRequest>({
      query: (queryArg) => ({
        url: `${PROJECTS_ENDPOINT}/versions/${queryArg.versionId}`,
        method: "PATCH",
        body: queryArg.body,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetProjectsSearchQuery,
  useGetPackageQuery,
  useUpdatePackageMutation,
  useGetVersionsQuery,
  useGetVersionQuery,
  useReprocessVersionMutation,
  useUpdateVersionMutation,
} = projectsApi;
